import { zodResolver } from "@hookform/resolvers/zod";
import { type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { Text } from "@alpacahq/ui/old";

import { FloatingButtons } from "../../../../components/common/FloatingButtons";
import type { IRAOnboardingProps } from "..";
import { Benefits } from "./Benefits";

const schema = z.object({
  is_stock_lending_enabled: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

export const StockLending = ({
  isStockLendingEnabled,
  onSaveSessionState,
}: IRAOnboardingProps) => {
  const { watch, setValue, handleSubmit } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      is_stock_lending_enabled: isStockLendingEnabled,
    },
  });

  const is_stock_lending_enabled = watch("is_stock_lending_enabled");

  const isNextButtonDisabled = is_stock_lending_enabled === undefined;

  const onSubmitValid: SubmitHandler<FormValues> = (data) => {
    onSaveSessionState({
      step: "agreements",
      isStockLendingEnabled: data.is_stock_lending_enabled,
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmitValid)}
      className="flex h-full flex-col gap-8"
    >
      <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4">
        <Benefits />
        <Text className="text-gray-500 text-xs">
          <a
            href="https://alpaca.markets/elite"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-700 underline"
          >
            *Alpaca Elite
          </a>{" "}
          users earn 50% of the interest income, while all users are guaranteed
          20% of the interest income. You can disable the Stock Lending Program
          at any time through the Plans & Features tab.
        </Text>
        <fieldset className="flex flex-col gap-3">
          <legend className="font-semibold text-base text-gray-800">
            Do you want to enable the Stock Lending Program?
          </legend>
          <div className="flex flex-start items-center gap-4">
            <label className="flex items-center gap-1">
              <input
                type="radio"
                id="yes"
                name="is_stock_lending_enabled"
                value="yes"
                checked={is_stock_lending_enabled === true}
                onChange={(event) => {
                  setValue("is_stock_lending_enabled", event.target.checked);
                }}
              />
              <span className="font-medium text-gray-700 text-sm">Yes</span>
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                id="no"
                name="is_stock_lending_enabled"
                value="no"
                checked={is_stock_lending_enabled === false}
                onChange={(event) => {
                  setValue("is_stock_lending_enabled", !event.target.checked);
                }}
              />
              <span className="font-medium text-gray-700 text-sm">No</span>
            </label>
          </div>
        </fieldset>
      </div>

      <FloatingButtons
        fixedOnBottom
        className="bg-white"
        next={{
          disabled: isNextButtonDisabled,
          label: "Continue",
          type: "submit",
        }}
        back={{
          onClick: () => {
            onSaveSessionState({
              step: "beneficiaries",
            });
          },
        }}
      />
    </form>
  );
};
